import { useQuery } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { Flame } from "lucide-react";
import { NavLink } from "react-router-dom";
import { cv } from "@/curative/api";
import { api, type NavCounts } from "@/lib/api";
import { signOut } from "@/lib/supabase";
import { isTouch } from "@/lib/touch";
import { useVoiceConfig } from "@/lib/voice";
import { cn } from "@/lib/utils";
import { isPalette, PALETTES, SWATCH, theme, useTheme, type ThemeMode } from "@/theme/theme";
import { CommandPalette } from "./CommandPalette";
import { MobileTabBar } from "./MobileNav";
import { Badge, Button } from "./ui";

/** §7.1 left nav, grouped and renamed for this business, each with a live count badge. */
const GROUPS: { heading: string; items: { to: string; label: string; count?: keyof NavCounts }[] }[] =
  [
    {
      heading: "Pipeline",
      items: [
        { to: "/", label: "Overview" },
        { to: "/leads", label: "Leads", count: "leads" },
        // Its own entry rather than a filter on Leads: the two lists answer different questions, and a
        // count is the cheapest way to make "how many have we lost" visible at all (D-32).
        { to: "/archived", label: "Archived", count: "archived" },
        { to: "/deals", label: "Deals", count: "deals" },
      ],
    },
    {
      heading: "Fulfillment",
      items: [
        { to: "/vendors", label: "Vendors", count: "vendors" },
        { to: "/engagements", label: "Engagements", count: "engagements" },
        { to: "/investors", label: "Investors", count: "investors" },
        { to: "/buyers", label: "Buyers", count: "buyers" },
      ],
    },
    // v2.1 T6.7 (G5 exception (d), D-55): Offers, and the whole Library group — Parcels, Documents,
    // Comps, County Recipes — left the nav until each has a page of its own. Their data is still
    // reached from the lead and deal tabs, and the routes still resolve.
    {
      heading: "Evidence",
      items: [
        { to: "/activity", label: "Activity log", count: "activity" },
        { to: "/analytics", label: "Analytics" },
        { to: "/agent-runs", label: "Agent runs", count: "agent_runs" },
        { to: "/ingest", label: "Ingest", count: "ingest" },
        // v2.2 P0.3 (e), D-67: the audit, the error ledger and the scheduler's record.
        { to: "/system", label: "System" },
      ],
    },
    // v2.0's one additive nav change (C0.1 b): a new group AFTER Evidence, with the four groups above
    // it untouched in content and order. G5 asserts exactly that.
    {
      heading: "CURATIVE",
      items: [
        { to: "/curative/today", label: "Today" },
        { to: "/curative/dashboard", label: "Dashboard" },
        { to: "/curative/inbox", label: "Sales Inbox", count: "cv_open_conversations" },
        { to: "/curative/import", label: "Import" },
        { to: "/curative/boards", label: "Boards", count: "cv_active_boards" },
        { to: "/curative/review", label: "Review", count: "cv_submitted_reviews" },
        { to: "/curative/smart-lists", label: "Smart Lists", count: "cv_my_follow_ups" },
      ],
    },
  ];

const MODES: { value: ThemeMode; label: string }[] = [
  { value: "light", label: "Light" },
  { value: "system", label: "System" },
  { value: "dark", label: "Dark" },
];

export function Shell({ children, email, role }: { children: ReactNode; email: string; role: string }) {
  const look = useTheme();
  // v2.10 P1.2: every Voice link targets hello@'s account (u/N or authuser), from the API's config.
  useVoiceConfig();
  const [touch] = useState(isTouch);
  const { data: counts } = useQuery({
    queryKey: ["nav-counts"],
    queryFn: () => api.navCounts(),
    refetchInterval: 60_000,
  });
  // The workspace palette (T2.4). A person's own override, if any, still wins inside the store.
  const { data: settings } = useQuery({
    queryKey: ["cv-settings"],
    queryFn: () => cv.settings(),
    staleTime: 300_000,
  });
  useEffect(() => {
    const palette = settings?.appearance?.palette;
    if (isPalette(palette)) theme.setWorkspace(palette);
  }, [settings]);

  // v2.10 P4.2: the account block, in the sidebar on a desktop and in the "More" sheet on a phone.
  const footer = (
    <>
          <div className="truncate" title={email}>
            {email}
          </div>
          <div className="flex items-center gap-1">
            {/* T2.2: green survives only where it means success, and the owner chip is that. */}
            <Badge tone={role === "owner" ? "ok" : "muted"}>{role}</Badge>
            {counts && counts.overdue > 0 && (
              <Badge tone="danger" title="overdue next actions">{counts.overdue} overdue</Badge>
            )}
          </div>
          <div
            className="flex rounded-md border p-0.5"
            role="group"
            aria-label="Theme"
            data-testid="theme-mode"
          >
            {MODES.map((m) => (
              <button
                key={m.value}
                type="button"
                aria-pressed={look.mode === m.value}
                data-testid={`theme-mode-${m.value}`}
                onClick={() => theme.setMode(m.value)}
                className={cn(
                  "flex-1 rounded px-1.5 py-0.5 text-[11px] transition-colors duration-[var(--duration-instant)]",
                  look.mode === m.value
                    ? "bg-surface-2 font-medium text-fg shadow-sm"
                    : "text-fg-muted hover:text-fg",
                )}
              >
                {m.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5" role="radiogroup" aria-label="Palette">
            {PALETTES.map((p) => (
              <button
                key={p}
                type="button"
                role="radio"
                aria-checked={look.palette === p}
                aria-label={`${p} palette`}
                title={`${p} palette (just for you)`}
                data-testid={`palette-${p}`}
                onClick={() => theme.setOverride(p === look.workspace ? null : p)}
                className={cn(
                  "size-3.5 rounded-full",
                  look.palette === p && "ring-2 ring-fg/60 ring-offset-2 ring-offset-surface",
                )}
                style={{ backgroundColor: SWATCH[p] }}
              />
            ))}
          </div>
          <Button size="sm" variant="ghost" onClick={() => void signOut()}>
            Sign out
          </Button>
          <p className="leading-snug">Nothing is sent on its own — a text goes only when you press Send.</p>
    </>
  );

  return (
    <div className="flex min-h-dvh">
      {/* v2.10 P4.2: the ⌘K palette and its hotkey are a keyboard's; a touch screen gets the tab bar. */}
      {!touch && <CommandPalette />}
      {/* v2.7 P2.2: the nav is the viewport tall and scrolls inside itself, so a page that fits the
          viewport (the board) is never made to scroll by a long nav. */}
      <nav className="sticky top-0 flex h-dvh w-56 shrink-0 flex-col border-r bg-surface max-md:hidden" aria-label="Main">
        <div className="flex h-11 items-center gap-2 border-b px-3">
          <span
            className="font-semibold text-primary"
            style={{ fontSize: 15, letterSpacing: "-0.01em" }}
            data-testid="wordmark"
          >
            Cornerstone
          </span>
        </div>
        <div className="flex-1 overflow-y-auto px-2 py-2">
          {GROUPS.map((group) => (
            <div key={group.heading} className="mb-3">
              <p
                className="px-2 pb-1 font-semibold uppercase text-fg-muted"
                style={{ fontSize: "10.5px", letterSpacing: "0.08em" }}
              >
                {group.heading}
              </p>
              <ul>
                {group.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.to === "/"}
                      className={({ isActive }) =>
                        cn(
                          "relative flex items-center justify-between gap-2 rounded px-2 py-1 text-[13px]",
                          "transition-colors duration-[var(--duration-instant)]",
                          isActive
                            ? "bg-primary/10 font-medium text-primary before:absolute before:inset-y-1 before:left-0 before:w-0.5 before:rounded-full before:bg-primary"
                            : "text-fg-muted hover:bg-surface-2 hover:text-fg",
                        )
                      }
                    >
                      <span>{item.label}</span>
                      {item.count && counts ? (
                        <span className="text-[11px] tabular-nums text-muted-foreground">
                          {counts[item.count]}
                        </span>
                      ) : null}
                    </NavLink>
                    {/* v2.2 P3.1: "Hot n" under Leads — a filter link, not a nav entry of its own. */}
                    {item.to === "/leads" && (
                      <NavLink
                        to="/leads?hot=1"
                        className="ml-3 flex items-center justify-between gap-2 rounded px-2 py-0.5 text-[12px] text-fg-muted hover:bg-surface-2 hover:text-fg"
                        data-testid="nav-hot"
                      >
                        <span className="inline-flex items-center gap-1">
                          <Flame className="size-3 text-orange-600" aria-hidden /> Hot
                        </span>
                        <span className="text-[11px] tabular-nums" data-testid="nav-hot-count">
                          {counts?.hot ?? 0}
                        </span>
                      </NavLink>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <footer className="space-y-2 border-t px-3 py-2 text-[11px] text-fg-muted">{footer}</footer>
      </nav>
      <main className="min-w-0 flex-1 overflow-x-hidden max-md:pb-[calc(3.5rem+env(safe-area-inset-bottom))]">
        {children}
      </main>
      <MobileTabBar groups={GROUPS} counts={counts} footer={footer} />
    </div>
  );
}
